/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo
} from 'react'

import { useAtomValue } from 'jotai'
import useWebSocket, { ReadyState } from 'react-use-websocket'

import { store } from '@/store'
import { loginStateAtom } from '@/store/user'

import useSWRSessionList from '../hooks/useSWRSessionList'
import {
  conversationListAtom,
  currentConversationAtom,
  messageDataAtom
} from '../store/message'
import { EConversationType } from '../types/message'
import type {
  CommandDataMap,
  WsCommand,
  WsRequest,
  WsResponse
} from '../types/ws-protocol'
import { sortSessions } from '../utils'

// 连接状态类型
export enum ConnectionStatus {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3
}

type WsContextType = {
  sendRequest: <T>(command: WsCommand, data?: any) => Promise<T>
  connectionStatus: ConnectionStatus
}

// 创建强类型化的上下文
const WsContext = createContext<WsContextType | null>(null)

// 自定义访问Hook
export const useWs = () => {
  const context = useContext(WsContext)
  if (!context) {
    throw new Error('useWs 必须在 WebsocketProvider 内使用')
  }
  return context
}

const WebsocketProvider: React.FC<{
  children: React.ReactNode
  wsEndpoint?: string
  onConnected?: () => void
  onDisconnected?: () => void
}> = ({
  children,
  wsEndpoint = import.meta.env.VITE_WS_ENDPOINT,
  onConnected,
  onDisconnected
}) => {
  const isLogin = useAtomValue(loginStateAtom)

  const wsEndpointUrl = useMemo(() => {
    return isLogin ? wsEndpoint : null
  }, [isLogin])

  // 状态管理
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.CLOSED
  )
  const { mutate, data: swrSessionList } = useSWRSessionList()

  // 存储pending请求
  const pendingRequestsRef = useRef<
    Record<
      string,
      {
        resolve: (value: any) => void
        reject: (reason: any) => void
        timeoutId: number
      }
    >
  >({})

  // 处理服务器推送消息
  const handleServerPush = useCallback(
    (data: WsResponse<CommandDataMap['PUSH_MESSAGE']>) => {
      switch (data.command) {
        case 'PUSH_MESSAGE': {
          // 处理推送消息

          const conversationList = store.get(conversationListAtom)
          const messageData = store.get(messageDataAtom)
          const currentConversation = store.get(currentConversationAtom)

          const targetConversation = conversationList?.find((listItem) => {
            if (data.response.data?.sender_type === EConversationType.GROUP) {
              return (
                listItem.session_id === data.response.data?.group_id &&
                listItem.session_type === data.response.data.sender_type
              )
            } else {
              return (
                listItem.session_id === data.response.data?.sender_id &&
                listItem.session_type === data.response.data.sender_type
              )
            }
          })

          // 全新的会话，需要重新获取会话列表数据
          if (!targetConversation) {
            mutate(swrSessionList, {
              revalidate: (pageData, key) =>
                key === '/imApi/session/list?page=1&size=20'
            })
            break
          }

          // 当前会话接收到了新的消息
          if (
            (currentConversation?.session_id ===
              data.response.data?.sender_id ||
              currentConversation?.session_id ===
                data.response.data?.group_id) &&
            currentConversation?.session_type ===
              data.response.data?.sender_type
          ) {
            if (messageData?.list) {
              messageData.list = [
                ...messageData.list,
                {
                  id: '',
                  created_at: String(data.response.data?.created_at),

                  message_content: data.response.data?.message_content || '',

                  message_type: data.response.data?.message_type || 0,

                  sender_user_id: data.response.data?.sender_id || ''
                }
              ]
              store.set(messageDataAtom, { ...messageData })
            } else {
              store.set(messageDataAtom, {
                list: [
                  {
                    id: '',
                    created_at: String(data.response.data?.created_at),

                    message_content: data.response.data?.message_content || '',

                    message_type: data.response.data?.message_type || 0,

                    sender_user_id: data.response.data?.sender_id || ''
                  }
                ],
                next: true
              })
            }

            const list = conversationList?.map((item) => {
              const dataSessionId =
                data.response.data?.sender_type === EConversationType.GROUP
                  ? data.response.data?.group_id
                  : data.response.data?.sender_id
              if (
                item.session_id === dataSessionId &&
                item?.session_type === data.response.data?.sender_type
              ) {
                item.last_message_content = data.response.data.message_content
                item.last_message_created_at = data.response.data.created_at
                item.last_message_sender_id = data.response.data.sender_id
                item.last_message_type = data.response.data.message_type
              }
              return item
            })

            let sortList = list
            if (list) {
              sortList = sortSessions(list)
            }

            store.set(conversationListAtom, sortList)

            setTimeout(() => {
              // 获取容器元素
              const container = document.getElementById('scrollableDivIM')
              // 将容器滚动到底部
              if (container) {
                container.scrollTop = container.scrollHeight
              }
            }, 100)

            break
          }

          if (targetConversation) {
            const list = conversationList?.map((item) => {
              const dataSessionId =
                data.response.data?.sender_type === EConversationType.GROUP
                  ? data.response.data?.group_id
                  : data.response.data?.sender_id
              if (
                item.session_id === dataSessionId &&
                item?.session_type === data.response.data?.sender_type
              ) {
                item.last_message_content = data.response.data.message_content
                item.last_message_created_at = data.response.data.created_at
                item.last_message_sender_id = data.response.data.sender_id
                item.last_message_type = data.response.data.message_type
              }
              return item
            })

            let sortList = list
            if (list) {
              sortList = sortSessions(list)
            }

            store.set(conversationListAtom, sortList)
            break
          }

          break
        }

        // 处理其他类型的推送...
      }
    },
    []
  )

  // 使用WebSocket
  const { sendMessage, lastMessage, readyState } = useWebSocket(wsEndpointUrl, {
    heartbeat: {
      message: 'PING',
      returnMessage: 'PONG',
      interval: 25 * 1000
    },
    shouldReconnect: () => true,
    reconnectAttempts: 10,
    reconnectInterval: (attemptNumber) => Math.min(attemptNumber * 1000, 10000),
    queryParams: {
      token: localStorage.getItem('token') || ''
    },
    onOpen: () => {
      setConnectionStatus(ConnectionStatus.OPEN)
      onConnected?.()
    },
    onClose: () => {
      setConnectionStatus(ConnectionStatus.CLOSED)
      onDisconnected?.()
    },
    onError: () => {
      // 仅在完全断开连接时才拒绝所有待处理请求
      if (readyState === ReadyState.CLOSED) {
        Object.entries(pendingRequestsRef.current).forEach(
          ([requestId, { reject, timeoutId }]) => {
            clearTimeout(timeoutId)
            reject({ code: -1, message: '连接错误' })
            delete pendingRequestsRef.current[requestId]
          }
        )
      }
    }
  })

  // 处理收到的消息
  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const data = JSON.parse(lastMessage.data) as WsResponse<
          CommandDataMap['PUSH_MESSAGE']
        >

        // 处理请求响应
        if (data.request_id && pendingRequestsRef.current[data.request_id]) {
          const { resolve, reject, timeoutId } =
            pendingRequestsRef.current[data.request_id]
          clearTimeout(timeoutId)

          if (data.response.code === 0) {
            resolve(data.response.data)
          } else {
            reject(data.response)
          }

          // 删除已处理的请求
          delete pendingRequestsRef.current[data.request_id]
        }
        // 处理服务端推送
        else if (data.command === 'PUSH_MESSAGE') {
          handleServerPush(data)
        }
        // 处理心跳响应(在这里不需要特殊处理，服务器的响应会被当作普通消息处理)
      } catch (error) {
        console.error('处理WebSocket消息时出错:', error)
      }
    }
  }, [lastMessage, handleServerPush])

  // 通用请求发送函数
  const sendRequest = useCallback(
    <T,>(command: WsCommand, data?: unknown): Promise<T> => {
      const requestId = crypto.randomUUID()

      // 如果连接已关闭，直接拒绝请求
      if (readyState !== ReadyState.OPEN) {
        return Promise.reject({
          code: -1,
          message: `连接未就绪，当前状态: ${ReadyState[readyState]}`
        })
      }

      return new Promise((resolve, reject) => {
        // 设置超时处理
        const timeoutId = window.setTimeout(() => {
          if (pendingRequestsRef.current[requestId]) {
            reject({ code: -1, message: '请求超时' })
            delete pendingRequestsRef.current[requestId]
          }
        }, 10000) // 10秒超时

        // 保存请求处理函数
        pendingRequestsRef.current[requestId] = { resolve, reject, timeoutId }

        // 发送请求
        const request: WsRequest = {
          command,
          request_id: requestId,
          data: data || null
        }

        // 发送消息
        sendMessage(JSON.stringify(request))
      })
    },
    [readyState, sendMessage]
  )

  // 清理资源
  useEffect(() => {
    return () => {
      // 清理所有待处理请求的超时计时器
      Object.values(pendingRequestsRef.current).forEach(({ timeoutId }) => {
        clearTimeout(timeoutId)
      })
    }
  }, [])

  // 更新connectionStatus以匹配readyState
  useEffect(() => {
    setConnectionStatus(readyState as unknown as ConnectionStatus)
  }, [readyState])

  return (
    <WsContext.Provider
      value={{
        sendRequest,
        connectionStatus
      }}
    >
      {children}
    </WsContext.Provider>
  )
}

export default WebsocketProvider
